import { useReducer } from 'react';

import { AppMembershipRoleEnum } from 'src/graphql/schema';
import { IAction } from 'src/utils/ts-utilities';

export type IPendingMember = {
  id: string;
  name: string;
  email: string;
  role: AppMembershipRoleEnum;
};

type INewPendingMember = Omit<IPendingMember, 'role'>;

enum ActionEnum {
  ADD_PENDING_MEMBER = 'add-pending-member',
  REMOVE_PENDING_MEMBER = 'remove-pending-member',
  UPDATE_PENDING_MEMBER_ROLE = 'update-pending-member-role',
  CLEAR_PENDING_MEMBERS = 'clear-pending-members',
}

type IActionReducer =
  | IAction<ActionEnum.ADD_PENDING_MEMBER, IPendingMember>
  | IAction<ActionEnum.REMOVE_PENDING_MEMBER, string>
  | IAction<ActionEnum.UPDATE_PENDING_MEMBER_ROLE, { id: string; role: AppMembershipRoleEnum }>
  | IAction<ActionEnum.CLEAR_PENDING_MEMBERS>;

export type IState = {
  pendingMembers: IPendingMember[];
};

const initialState: IState = {
  pendingMembers: [],
};

const reducer = (state: IState, action: IActionReducer): IState => {
  switch (action.type) {
    case ActionEnum.ADD_PENDING_MEMBER:
      return {
        ...state,
        pendingMembers: [...state.pendingMembers, action.payload],
      };
    case ActionEnum.REMOVE_PENDING_MEMBER:
      return {
        ...state,
        pendingMembers: state.pendingMembers.filter(({ id }) => id !== action.payload),
      };
    case ActionEnum.UPDATE_PENDING_MEMBER_ROLE:
      return {
        ...state,
        pendingMembers: state.pendingMembers.map((member) =>
          member.id === action.payload.id ? { ...member, role: action.payload.role } : member,
        ),
      };
    case ActionEnum.CLEAR_PENDING_MEMBERS:
      return {
        ...state,
        pendingMembers: [],
      };
    default:
      return state;
  }
};

export const useAppMembershipsManagementSlice = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const addPendingMember = (member: INewPendingMember) =>
    dispatch({ type: ActionEnum.ADD_PENDING_MEMBER, payload: { ...member, role: AppMembershipRoleEnum.Viewer } });
  const removePendingMember = (id: string) => dispatch({ type: ActionEnum.REMOVE_PENDING_MEMBER, payload: id });
  const updatePendingMemberRole = (id: string, role: AppMembershipRoleEnum) =>
    dispatch({ type: ActionEnum.UPDATE_PENDING_MEMBER_ROLE, payload: { id, role } });
  const clearPendingMembers = () => dispatch({ type: ActionEnum.CLEAR_PENDING_MEMBERS, payload: {} });

  return {
    ...state,
    addPendingMember,
    removePendingMember,
    updatePendingMemberRole,
    clearPendingMembers,
  };
};
